import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import FieldToken from './FieldToken.jsx';
import Drawer from '../../../../components/v2/Drawer.jsx';

/** Available template field definitions */
const TEMPLATE_FIELDS = [
  { key: 'device_name', label: 'Device Name', example: 'AIO Station' },
  { key: 'device_description', label: 'Device Description', example: 'Bathroom' },
  { key: 'manufacture_uuid', label: 'Serial Number', example: '4555684' },
  { key: 'resident_first_name', label: 'Resident First Name', example: 'Daniel' },
  { key: 'resident_last_name', label: 'Resident Last Name', example: 'Avis' },
  { key: 'resident_name', label: 'Resident Full Name', example: 'Daniel Avis' },
  { key: 'area_name', label: 'Area Name', example: 'Mountain Spring 301' },
  { key: 'near_area_name', label: 'Nearby Area', example: 'West Cafe' },
  { key: 'event_category', label: 'Event Category', example: 'Emergency' },
  { key: 'event_type', label: 'Event Type', example: 'Pull Cord Pulled' },
  { key: 'stage', label: 'Event Stage', example: 'CREATE' },
];

/** Example event data for live preview */
const EXAMPLE_EVENT = {
  device_name: 'AIO Station',
  device_description: 'Bathroom',
  manufacture_uuid: '4555684',
  resident_first_name: 'Daniel',
  resident_last_name: 'Avis',
  resident_name: 'Daniel Avis',
  area_name: 'Mountain Spring 301',
  near_area_name: 'West Cafe',
  event_category: 'Emergency',
  event_type: 'Pull Cord Pulled',
  stage: 'CREATE',
};

/**
 * TemplateDrawer — slide-out drawer for adding/editing alert text templates.
 *
 * Props:
 *   isOpen           — drawer visibility
 *   onClose          — close callback
 *   template         — null for Add mode, object for Edit mode
 *   onSave           — (formData) => Promise
 *   availableTypes   — array of { id, name } output types available to add
 */
const TemplateDrawer = ({ isOpen, onClose, template, onSave, availableTypes }) => {
  const isEditMode = !!template;

  // Form state
  const [outputTypeId, setOutputTypeId] = useState('');
  const [tokens, setTokens] = useState([]);       // [{ id, key, label, customValue? }]
  const [customValues, setCustomValues] = useState({}); // { tokenId: string }
  const [saving, setSaving] = useState(false);

  const firstInputRef = useRef(null);
  const tokenIdCounter = useRef(0);

  // Generate unique token ID
  const nextTokenId = () => {
    tokenIdCounter.current += 1;
    return `tok_${Date.now()}_${tokenIdCounter.current}`;
  };

  // Populate form when drawer opens
  useEffect(() => {
    if (!isOpen) return;

    if (template) {
      // Edit mode — parse text_format CSV
      setOutputTypeId(String(template.output_type_id));
      const parsed = parseTextFormat(template.text_format || '');
      setTokens(parsed.tokens);
      setCustomValues(parsed.customValues);
    } else {
      // Add mode — blank
      setOutputTypeId('');
      setTokens([]);
      setCustomValues({});
    }

    tokenIdCounter.current = 0;

    // Auto-focus first input
    setTimeout(() => firstInputRef.current?.focus(), 200);
  }, [isOpen, template]);

  // Parse CSV text_format into tokens array + customValues map
  const parseTextFormat = (csv) => {
    const parts = csv.split(',').filter(Boolean);
    const newTokens = [];
    const newCustom = {};

    parts.forEach((part) => {
      const id = nextTokenId();
      if (part.startsWith('custom.')) {
        const val = part.replace('custom.', '');
        newTokens.push({ id, key: 'custom', label: 'Custom Text' });
        newCustom[id] = val;
      } else {
        const fieldDef = TEMPLATE_FIELDS.find((f) => f.key === part);
        newTokens.push({
          id,
          key: part,
          label: fieldDef ? fieldDef.label : part,
        });
      }
    });

    return { tokens: newTokens, customValues: newCustom };
  };

  // Check if a field is already added (non-custom fields can only appear once)
  const isFieldAdded = useCallback(
    (key) => {
      if (key === 'custom') return false; // custom can be added multiple times
      return tokens.some((t) => t.key === key);
    },
    [tokens]
  );

  // Add a field token
  const handleAddField = useCallback((field) => {
    if (field.key !== 'custom' && isFieldAdded(field.key)) return;

    const id = nextTokenId();
    if (field.key === 'custom') {
      setTokens((prev) => [...prev, { id, key: 'custom', label: 'Custom Text' }]);
      setCustomValues((prev) => ({ ...prev, [id]: '' }));
    } else {
      setTokens((prev) => [...prev, { id, key: field.key, label: field.label }]);
    }
  }, [isFieldAdded]);

  // Remove a token
  const handleRemoveToken = useCallback((tokenId) => {
    setTokens((prev) => prev.filter((t) => t.id !== tokenId));
    setCustomValues((prev) => {
      const next = { ...prev };
      delete next[tokenId];
      return next;
    });
  }, []);

  // Update custom text value
  const handleCustomChange = useCallback((tokenId, value) => {
    setCustomValues((prev) => ({ ...prev, [tokenId]: value }));
  }, []);

  // Move token up/down
  const handleMoveToken = useCallback((index, direction) => {
    setTokens((prev) => {
      const next = [...prev];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }, []);

  // Live preview
  const preview = useMemo(() => {
    if (tokens.length === 0) return '';
    return tokens.map((t) => {
      if (t.key === 'custom') return customValues[t.id] || '';
      return EXAMPLE_EVENT[t.key] || t.key;
    }).join(' ').trim();
  }, [tokens, customValues]);

  // Validation
  const isValid = useMemo(() => {
    if (!isEditMode && !outputTypeId) return false;
    if (tokens.length === 0) return false;
    // All custom tokens must have non-empty values
    return tokens
      .filter((t) => t.key === 'custom')
      .every((t) => (customValues[t.id] || '').trim().length > 0);
  }, [isEditMode, outputTypeId, tokens, customValues]);

  // Submit
  const handleSubmit = async () => {
    if (!isValid || saving) return;
    setSaving(true);

    const textFormat = tokens.map((t) => {
      if (t.key === 'custom') return `custom.${customValues[t.id] || ''}`;
      return t.key;
    }).join(',');

    const formData = {
      output_type_id: isEditMode ? template.output_type_id : parseInt(outputTypeId, 10),
      text_format: textFormat,
    };

    if (isEditMode) {
      formData.template_id = template.template_id;
      formData.oldTextFormat = template.text_format;
    }

    await onSave(formData);
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="ct-drawer__overlay" onClick={onClose} />
      <Drawer
        open={isOpen}
        onClose={onClose}
        classPrefix="ct"
        size="lg"
        backdrop={false}
        panelAs="aside"
        ariaLabel={isEditMode ? 'Edit Template' : 'New Template'}
        title={isEditMode ? 'Edit Template' : 'New Template'}
        footer={(
          <>
            <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="dp-btn dp-btn--primary"
              disabled={!isValid || saving}
              onClick={handleSubmit}
            >
              {saving ? 'Saving\u2026' : isEditMode ? 'Save Changes' : 'Create Template'}
            </button>

          </>
        )}
      >
        {/* Output Type */}
        <div className="ct-drawer__section-card">
          <h3 className="ct-drawer__section-title">Output Type</h3>
          {isEditMode ? (
            <div className="ct-drawer__readonly-value">
              {template.output_type_name || `Type ${template.output_type_id}`}
            </div>
          ) : (
            <select
              ref={firstInputRef}
              className="ct-drawer__select"
              value={outputTypeId}
              onChange={(e) => setOutputTypeId(e.target.value)}
            >
              <option value="">Select output type...</option>
              {(availableTypes || []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Alert Text Preview */}
        <div className="ct-drawer__section-card">
          <h3 className="ct-drawer__section-title">Alert Text Preview</h3>
          <div className="ct-preview-card">
            {preview ? (
              <p className="ct-preview-card__text">{preview}</p>
            ) : (
              <p className="ct-preview-card__placeholder">No template items selected</p>
            )}
          </div>
        </div>

        {/* Template Builder */}
        <div className="ct-drawer__section-card">
          <h3 className="ct-drawer__section-title">Template Composition</h3>
          <div className="ct-builder__composition">
            {tokens.length === 0 ? (
              <div className="ct-builder__empty">
                <p>Click fields below to build your template</p>
              </div>
            ) : (
              <div className="ct-builder__tokens">
                {tokens.map((t, idx) => (
                  <div key={t.id} className="ct-builder__token-row">
                    <div className="ct-builder__token-order">
                      <button
                        type="button"
                        className="ct-builder__move-btn"
                        disabled={idx === 0}
                        onClick={() => handleMoveToken(idx, -1)}
                        aria-label="Move up"
                      >
                        <i className="fe fe-chevron-up" />
                      </button>
                      <button
                        type="button"
                        className="ct-builder__move-btn"
                        disabled={idx === tokens.length - 1}
                        onClick={() => handleMoveToken(idx, 1)}
                        aria-label="Move down"
                      >
                        <i className="fe fe-chevron-down" />
                      </button>
                    </div>
                    <FieldToken
                      label={t.label}
                      isCustom={t.key === 'custom'}
                      customValue={customValues[t.id]}
                      onCustomChange={(val) => handleCustomChange(t.id, val)}
                      onRemove={() => handleRemoveToken(t.id)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Available Fields */}
        <div className="ct-drawer__section-card">
          <h3 className="ct-drawer__section-title">Available Fields</h3>
          <div className="ct-builder__fields">
            {TEMPLATE_FIELDS.map((field) => {
              const disabled = isFieldAdded(field.key);
              return (
                <button
                  key={field.key}
                  type="button"
                  className={`ct-field-btn${disabled ? ' ct-field-btn--disabled' : ''}`}
                  disabled={disabled}
                  onClick={() => handleAddField(field)}
                >
                  <span className="ct-field-btn__label">
                    {field.label}
                    {disabled ? <span className="ct-field-btn__added">(Added)</span> : null}
                  </span>
                  <span className="ct-field-btn__example">{field.example}</span>
                </button>
              );
            })}
            {/* Custom Text — always available */}
            <button
              type="button"
              className="ct-field-btn ct-field-btn--custom"
              onClick={() => handleAddField({ key: 'custom', label: 'Custom Text' })}
            >
              <span className="ct-field-btn__label">
                <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="14" height="14" style={{ marginRight: '4px', flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Custom Text
              </span>
              <span className="ct-field-btn__example">Static text between fields</span>
            </button>
          </div>
        </div>

      </Drawer>
    </>
  );
};

export default TemplateDrawer;
